import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Row, Col, Statistic, Table, Button, Modal, Form, Input, Select,
  InputNumber, message, Space, Tag, Popconfirm, Tabs, List, DatePicker, Typography, AutoComplete, Segmented, Empty
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserAddOutlined, HistoryOutlined, SettingOutlined, AppstoreOutlined, BarsOutlined, SendOutlined, SwapOutlined, ShrinkOutlined, ArrowsAltOutlined, TeamOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import AgentTierDemo from './AgentTierDemo';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// 代理级别：费用=升级费用；rate=推荐手续费费率
const AGENT_RATE = { 普通代理: 0.02, 银牌代理: 0.04, 金牌代理: 0.06, 钻石代理: 0.08, 铂金代理: 0.10 };
const AGENT_FEE_PRICE = { 普通代理: 298, 银牌代理: 598, 金牌代理: 998, 钻石代理: 1998, 铂金代理: 3998 };
const levelRateText = (l) => (AGENT_RATE[l] != null ? `${(AGENT_RATE[l] * 100).toFixed(0)}%` : '');
const agentRate = (l) => (AGENT_RATE[l] != null ? AGENT_RATE[l] : 0.02);
// 推荐费 = 推荐客户总金额 × 级别费率（按级别自动计算）；优先用后端算好的 referral_fee
const agentRefFee = (a) => (
  a.referral_fee != null
    ? parseFloat(a.referral_fee)
    : parseFloat(a.network_ts_total || 0) * agentRate(a.agent_level)
);
const agentRecruitReward = (a) => parseFloat(a.recruit_reward || 0); // 推荐代理奖励=下级会员费一半之和
const agentDownlineReward = (a) => parseFloat(a.downline_reward || 0); // 下级贡献奖励
// 总收益 = 推荐费 + 推荐代理奖励 + 下级贡献奖励
const agentTotalReward = (a) => (
  a.total_reward != null
    ? parseFloat(a.total_reward)
    : agentRefFee(a) + agentRecruitReward(a) + agentDownlineReward(a)
);
// 未结算 = 总收益 − 已结算（自动）
const agentUnsettled = (a) => agentTotalReward(a) - parseFloat(a.settled_fee || 0);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const TsAgentAdminPanel = () => {
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentLevels, setAgentLevels] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('customers');
  const narrowDefault = typeof window !== 'undefined' && window.innerWidth < 900 ? 'card' : 'table';
  const [customerView, setCustomerView] = useState(narrowDefault);
  const [agentView, setAgentView] = useState(narrowDefault);
  const [detailExpanded, setDetailExpanded] = useState(false); // 客户明细收缩/展开，默认收缩
  const customerViewManualRef = useRef(false);
  const agentViewManualRef = useRef(false);
  // 会员搜索防竞态：只采用「最新一次输入」的结果，丢弃过期请求
  const agentNameQRef = useRef('');
  const agentReferrerQRef = useRef('');
  const referrerQRef = useRef('');
  const memberPickQRef = useRef('');

  const [customerModal, setCustomerModal] = useState(false);
  const [agentModal, setAgentModal] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [followupModal, setFollowupModal] = useState(false);
  const [typeMgrModal, setTypeMgrModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [memberPickType, setMemberPickType] = useState('customer');
  const [memberSearch, setMemberSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [referrerOptions, setReferrerOptions] = useState([]);
  const [agentNameOpts, setAgentNameOpts] = useState([]);
  const [agentReferrerOpts, setAgentReferrerOpts] = useState([]);
  const [agentParent, setAgentParent] = useState(null); // 新增下级代理时的上级
  const [referredModal, setReferredModal] = useState(false);
  const [referredAgent, setReferredAgent] = useState(null);
  const [referredList, setReferredList] = useState([]);
  const [referredLoading, setReferredLoading] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [subParent, setSubParent] = useState(null);
  const [subList, setSubList] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [followupCustomer, setFollowupCustomer] = useState(null);

  const [customerForm] = Form.useForm();
  const [agentForm] = Form.useForm();
  const [followupForm] = Form.useForm();
  const [typeForm] = Form.useForm();

  const loadMeta = useCallback(async () => {
    const res = await axios.get('/ts/meta/options', { headers: authHeaders() });
    if (res.data.success) {
      setAgentLevels(res.data.agentLevels || []);
    }
  }, []);

  const loadCustomerTypes = useCallback(async () => {
    const res = await axios.get('/ts/admin/customer-types', { headers: authHeaders() });
    if (res.data.success) setCustomerTypes(res.data.types || []);
  }, []);

  const loadStats = useCallback(async () => {
    const res = await axios.get('/ts/admin/dashboard', { headers: authHeaders() });
    if (res.data.success) setStats(res.data.stats);
  }, []);

  const loadCustomers = useCallback(async () => {
    const res = await axios.get('/ts/admin/customers', { headers: authHeaders() });
    if (res.data.success) setCustomers(res.data.customers);
  }, []);

  const loadAgents = useCallback(async () => {
    const [agentsRes, optRes] = await Promise.all([
      axios.get('/ts/admin/agents', { headers: authHeaders() }),
      axios.get('/ts/admin/agents/options', { headers: authHeaders() }),
    ]);
    if (agentsRes.data.success) setAgents(agentsRes.data.agents);
    if (optRes.data.success) setAgentOptions(optRes.data.agents);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadStats(), loadCustomers(), loadAgents(), loadMeta(), loadCustomerTypes()]);
    } finally {
      setLoading(false);
    }
  }, [loadStats, loadCustomers, loadAgents, loadMeta, loadCustomerTypes]);

  useEffect(() => { refresh(); }, [refresh]);

  // 窄屏自动切卡片；用户手动切换后不再自动改
  useEffect(() => {
    const onResize = () => {
      const v = window.innerWidth < 900 ? 'card' : 'table';
      if (!customerViewManualRef.current) setCustomerView(v);
      if (!agentViewManualRef.current) setAgentView(v);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const changeCustomerView = (v) => { customerViewManualRef.current = true; setCustomerView(v); };
  const changeAgentView = (v) => { agentViewManualRef.current = true; setAgentView(v); };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 12 }, (_, i) => currentYear - 8 + i).map((y) => ({ value: y, label: String(y) }));

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => message.success('已复制到剪贴板'),
        () => message.info({ content: text, duration: 6 })
      );
    } else {
      message.info({ content: text, duration: 6 });
    }
  };

  const fmtAmt = (n) => String(Math.round(Number(n) || 0));

  // 收款：转账话术
  const sendReceive = (r) => {
    const ts = Number(r.ts_amount) || 0;
    copyText(`${r.customer_name || ''}，${r.id_card || ''}，${r.phone || ''}，TS金额：${fmtAmt(ts)}，手续费${fmtAmt(ts * 0.24)}，现在转账来，以进入正式申请`);
  };

  // 发送：复制固定话术到剪贴板
  const sendCopy = (r) => {
    copyText(`${r.customer_name || ''}，${r.id_card || ''}，${r.phone || ''}，您的TS已提交，具体金额为：${r.refund_note || ''}，退往：${r.bank_card || ''}`);
  };

  // 变动：按 实退金额 与 TS金额 的大小关系生成话术
  const sendChange = (r) => {
    const ts = Number(r.ts_amount) || 0;
    const refund = Number(r.refund_amount) || 0;
    if (refund > ts) {
      const diff = refund - ts;
      copyText(`测算金额${fmtAmt(ts)}，实退金额${fmtAmt(refund)}，为您争取多退${fmtAmt(diff)}，现在补来手续费${fmtAmt(diff * 0.24)}`);
    } else if (refund < ts) {
      const diff = ts - refund;
      copyText(`测算金额${fmtAmt(ts)}，实退金额${fmtAmt(refund)}，少退了${fmtAmt(diff)}，现在退还手续费${fmtAmt(diff * 0.24)}`);
    } else {
      message.info('实退金额与测算金额一致，无变动');
    }
  };

  const searchMembers = async (q) => {
    const query = (q || '').trim();
    memberPickQRef.current = query; // 防竞态：只采用最新一次搜索的结果
    const res = await axios.get('/ts/admin/members/available', {
      headers: authHeaders(),
      params: { type: memberPickType, q: query, limit: 30 },
    });
    if (memberPickQRef.current !== query) return; // 丢弃过期结果
    if (res.data.success) setMembers(res.data.members);
  };

  const openMemberPick = (type) => {
    setMemberPickType(type);
    setMemberModal(true);
    setMemberSearch('');
    searchMembers('');
  };

  const selectMember = async (member) => {
    setMemberModal(false);
    if (memberPickType === 'customer') {
      setEditingCustomer(null);
      customerForm.setFieldsValue({
        user_id: member.id,
        customer_name: member.nickname,
        phone: /^1[3-9]\d{9}$/.test(member.phone || '') ? member.phone : '',
      });
      setCustomerModal(true);
    } else {
      setEditingAgent(null);
      agentForm.setFieldsValue({
        user_id: member.id,
        agent_name: member.nickname,
        phone: /^1[3-9]\d{9}$/.test(member.phone || '') ? member.phone : '',
      });
      setAgentModal(true);
    }
  };

  // 推荐人：自由文本，onSearch 时联想已注册会员；手动输入即清空会员关联
  const fetchReferrerOptions = async (q) => {
    customerForm.setFieldsValue({ referrer_user_id: null });
    const query = (q || '').trim();
    referrerQRef.current = query;
    if (!query) { setReferrerOptions([]); return; }
    try {
      const res = await axios.get('/ts/admin/members/available', {
        headers: authHeaders(),
        params: { type: 'referrer', q: query, limit: 20 },
      });
      if (referrerQRef.current !== query) return; // 丢弃过期结果
      if (res.data.success) {
        setReferrerOptions((res.data.members || []).map((m) => ({
          value: m.nickname || m.phone || `会员${m.id}`,
          label: `${m.nickname || '未命名'}（${m.phone || '无手机'} · 会员）`,
          memberId: m.id,
        })));
      }
    } catch (e) { /* ignore */ }
  };

  const openAddCustomer = () => {
    setEditingCustomer(null);
    customerForm.resetFields();
    setReferrerOptions([]);
    setCustomerModal(true);
  };

  // ---- 客户类型管理 ----
  const submitType = async (values) => {
    try {
      if (editingType) {
        await axios.put(`/ts/admin/customer-types/${editingType.id}`, values, { headers: authHeaders() });
        message.success('类型已更新');
      } else {
        await axios.post('/ts/admin/customer-types', values, { headers: authHeaders() });
        message.success('类型已添加');
      }
      typeForm.resetFields();
      setEditingType(null);
      loadCustomerTypes();
    } catch (e) {
      message.error(e.response?.data?.message || '保存类型失败');
    }
  };

  const deleteType = async (id) => {
    try {
      await axios.delete(`/ts/admin/customer-types/${id}`, { headers: authHeaders() });
      message.success('类型已删除');
      loadCustomerTypes();
    } catch (e) {
      message.error(e.response?.data?.message || '删除类型失败');
    }
  };

  const openEditCustomer = (record) => {
    setEditingCustomer(record);
    // 若推荐人已关联会员，预置一个选项以便展示
    setReferrerOptions(
      record.referrer_user_id
        ? [{ value: record.referrer_name || record.referrer_member_name || '', label: `${record.referrer_member_name || record.referrer_name}（会员）`, memberId: record.referrer_user_id }]
        : []
    );
    customerForm.setFieldsValue({
      customer_name: record.customer_name,
      region: record.region,
      id_card: record.id_card,
      phone: record.phone,
      year: record.year || undefined,
      ts_amount: record.ts_amount,
      customer_type: record.customer_type,
      basic_profile: record.basic_profile,
      fee_amount: record.fee_amount,
      paid_amount: record.paid_amount,
      refund_amount: record.refund_amount,
      bank_card: record.bank_card,
      refund_note: record.refund_note,
      referrer_name: record.referrer_name || record.referrer_member_name || undefined,
      referrer_user_id: record.referrer_user_id || null,
    });
    setCustomerModal(true);
  };

  const saveCustomer = async (values) => {
    try {
      if (editingCustomer) {
        await axios.put(`/ts/admin/customers/${editingCustomer.id}`, values, { headers: authHeaders() });
        message.success('客户已更新');
      } else {
        await axios.post('/ts/admin/customers', values, { headers: authHeaders() });
        message.success('客户已创建');
      }
      setCustomerModal(false);
      customerForm.resetFields();
      refresh();
    } catch (e) {
      message.error(e.response?.data?.message || '保存失败');
    }
  };

  const saveAgent = async (values) => {
    const payload = {
      ...values,
      last_settlement_at: values.last_settlement_at
        ? values.last_settlement_at.format('YYYY-MM-DD HH:mm:ss')
        : null,
    };
    try {
      if (editingAgent) {
        await axios.put(`/ts/admin/agents/${editingAgent.id}`, payload, { headers: authHeaders() });
        message.success('代理人已更新');
      } else {
        await axios.post('/ts/admin/agents', payload, { headers: authHeaders() });
        message.success('代理人已创建');
      }
      setAgentModal(false);
      agentForm.resetFields();
      refresh();
    } catch (e) {
      message.error(e.response?.data?.message || '保存失败');
    }
  };

  const openFollowups = async (customer) => {
    setFollowupCustomer(customer);
    const res = await axios.get(`/ts/admin/customers/${customer.id}/followups`, { headers: authHeaders() });
    if (res.data.success) setFollowups(res.data.followups);
    setFollowupModal(true);
  };

  const addFollowup = async (values) => {
    await axios.post(
      `/ts/admin/customers/${followupCustomer.id}/followups`,
      { content: values.content, followup_date: values.followup_date?.format('YYYY-MM-DD') },
      { headers: authHeaders() }
    );
    message.success('跟进已添加');
    followupForm.resetFields();
    openFollowups(followupCustomer);
  };

  const customerColumns = [
    { title: '地区', dataIndex: 'region', key: 'region', width: 90, render: (v) => v || '-' },
    { title: '客户姓名', dataIndex: 'customer_name', key: 'name' },
    { title: '年份', dataIndex: 'year', key: 'year', width: 70, render: (y) => y || '-' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    { title: '身份证号', dataIndex: 'id_card', key: 'id_card', ellipsis: true },
    {
      title: 'TS金额',
      dataIndex: 'ts_amount',
      key: 'ts_amount',
      render: (v) => <Text strong>¥{parseFloat(v || 0).toFixed(0)}</Text>,
    },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      render: (v) => <Text style={{ color: '#6366F1' }}>¥{parseFloat(v || 0).toFixed(0)}</Text>,
    },
    {
      title: '已付',
      dataIndex: 'paid_amount',
      key: 'paid',
      render: (v) => <Text type="success">¥{parseFloat(v || 0).toFixed(0)}</Text>,
    },
    {
      title: '未付',
      key: 'unpaid',
      render: (_, r) => {
        const unpaid = parseFloat(r.fee_amount || 0) - parseFloat(r.paid_amount || 0);
        return <Text type={unpaid > 0 ? 'warning' : undefined}>¥{unpaid.toFixed(0)}</Text>;
      },
    },
    {
      title: '实退金额',
      dataIndex: 'refund_amount',
      render: (v) => <Text>¥{parseFloat(v || 0).toFixed(0)}</Text>,
    },
    { title: '银行卡', dataIndex: 'bank_card', width: 160, render: (v) => (v ? <Text copyable style={{ fontSize: 12 }}>{v}</Text> : '-') },
    { title: '实退提示', dataIndex: 'refund_note', width: 140, ellipsis: true, render: (v) => v || '-' },
    { title: '类型', dataIndex: 'customer_type', render: (t) => (t ? <Tag>{t}</Tag> : '-') },
    {
      title: '推荐人',
      key: 'referrer',
      render: (_, r) => {
        const name = r.referrer_member_name || r.referrer_name;
        if (!name) return '-';
        return r.referrer_user_id
          ? <span>{name} <Tag color="blue" style={{ marginInlineStart: 4 }}>会员</Tag></span>
          : name;
      },
    },
    { title: '跟进', dataIndex: 'followup_count', key: 'followup', render: (c) => `${c || 0} 条` },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', flexWrap: 'nowrap', whiteSpace: 'nowrap', gap: 8 }}>
          <a style={{ fontWeight: 600, color: '#13c2c2' }} onClick={() => sendReceive(record)}>收款</a>
          <a style={{ fontWeight: 600 }} onClick={() => sendCopy(record)}>发送</a>
          <a style={{ fontWeight: 600, color: '#722ed1' }} onClick={() => sendChange(record)}>变动</a>
          <a onClick={() => openFollowups(record)}>跟进</a>
          <a onClick={() => openEditCustomer(record)}>编辑</a>
          <Popconfirm title="确定删除？" onConfirm={async () => {
            await axios.delete(`/ts/admin/customers/${record.id}`, { headers: authHeaders() });
            message.success('已删除');
            refresh();
          }}>
            <a style={{ color: '#ff4d4f' }}>删除</a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // 收缩时仅显示这几列；展开显示全部
  const COLLAPSED_KEYS = new Set(['region', 'name', 'ts_amount', 'paid', 'followup']);
  const displayedCustomerColumns = detailExpanded
    ? customerColumns
    : customerColumns.filter((c) => COLLAPSED_KEYS.has(c.key));

  const renderCustomerCard = (r) => {
    const referrer = r.referrer_member_name || r.referrer_name;
    return (
      <Col xs={24} sm={12} lg={8} key={r.id}>
        <Card size="small" style={{ borderRadius: 12, height: '100%' }} styles={{ body: { padding: 14 } }}>
          {/* 头部：地区 + 姓名 + TS金额（收缩/展开均显示） */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {r.region ? <Tag color="cyan">{r.region}</Tag> : null}
                <span style={{ fontSize: 16, fontWeight: 600 }}>{r.customer_name}</span>
              </div>
              {detailExpanded && (
                <div style={{ marginTop: 4 }}>
                  {r.year ? <Tag color="purple">{r.year}年</Tag> : null}
                  {r.customer_type ? <Tag>{r.customer_type}</Tag> : null}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>TS金额</div>
              <Text strong style={{ fontSize: 16, color: '#6366F1', whiteSpace: 'nowrap' }}>¥{parseFloat(r.ts_amount || 0).toFixed(0)}</Text>
            </div>
          </div>

          {!detailExpanded ? (
            <div style={{ marginTop: 8, fontSize: 13, color: '#595959' }}>
              已付 <Text type="success">¥{parseFloat(r.paid_amount || 0).toFixed(0)}</Text>　·　跟进 {r.followup_count || 0} 条
            </div>
          ) : (
            <>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, textAlign: 'center' }}>
                <div style={{ flex: 1, background: '#f5f3ff', borderRadius: 8, padding: '6px 4px' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>手续费</div>
                  <div style={{ fontWeight: 600, color: '#6366F1' }}>¥{parseFloat(r.fee_amount || 0).toFixed(0)}</div>
                </div>
                <div style={{ flex: 1, background: '#f6ffed', borderRadius: 8, padding: '6px 4px' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>已付</div>
                  <div style={{ fontWeight: 600, color: '#52c41a' }}>¥{parseFloat(r.paid_amount || 0).toFixed(0)}</div>
                </div>
                <div style={{ flex: 1, background: '#fff7e6', borderRadius: 8, padding: '6px 4px' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>未付</div>
                  <div style={{ fontWeight: 600, color: '#fa8c16' }}>¥{(parseFloat(r.fee_amount || 0) - parseFloat(r.paid_amount || 0)).toFixed(0)}</div>
                </div>
              </div>
              <div style={{ marginTop: 10, fontSize: 13, color: '#595959', lineHeight: 2 }}>
                <div>📱 手机：{r.phone || '-'}</div>
                <div>🪪 身份证：{r.id_card || '-'}</div>
                <div>💵 实退金额：¥{parseFloat(r.refund_amount || 0).toFixed(0)}</div>
                <div>💳 银行卡：{r.bank_card || '-'}</div>
                {r.refund_note ? <div>📌 实退提示：{r.refund_note}</div> : null}
                <div>🤝 推荐人：{referrer || '-'} {r.referrer_user_id ? <Tag color="blue" style={{ marginInlineStart: 4 }}>会员</Tag> : null}</div>
                <div>📝 跟进：{r.followup_count || 0} 条</div>
              </div>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, borderTop: '1px solid #f5f5f5', paddingTop: 8 }}>
                <Button type="link" size="small" style={{ color: '#13c2c2' }} onClick={() => sendReceive(r)}>收款</Button>
                <Button type="link" size="small" icon={<SendOutlined />} onClick={() => sendCopy(r)}>发送</Button>
                <Button type="link" size="small" style={{ color: '#722ed1' }} icon={<SwapOutlined />} onClick={() => sendChange(r)}>变动</Button>
                <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => openFollowups(r)}>跟进</Button>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditCustomer(r)}>编辑</Button>
                <Popconfirm title="确定删除？" onConfirm={async () => {
                  await axios.delete(`/ts/admin/customers/${r.id}`, { headers: authHeaders() });
                  message.success('已删除'); refresh();
                }}>
                  <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                </Popconfirm>
              </div>
            </>
          )}
        </Card>
      </Col>
    );
  };

  // 代理人姓名 / 推荐人：自由文本，onSearch 联想已注册会员
  const memberOptions = (members) => (members || []).map((m) => ({
    value: m.nickname || m.phone || `会员${m.id}`,
    label: `${m.nickname || '未命名'}（${m.phone || '无手机'} · 会员）`,
    memberId: m.id,
  }));

  const fetchAgentNameOpts = async (q) => {
    agentForm.setFieldsValue({ user_id: null });
    const query = (q || '').trim();
    agentNameQRef.current = query;
    if (!query) { setAgentNameOpts([]); return; }
    try {
      const res = await axios.get('/ts/admin/members/available', { headers: authHeaders(), params: { type: 'referrer', q: query, limit: 20 } });
      if (agentNameQRef.current !== query) return; // 已有更新的输入，丢弃过期结果
      if (res.data.success) setAgentNameOpts(memberOptions(res.data.members));
    } catch (e) { /* ignore */ }
  };
  // 上级 = 另一个代理人。按 代理名/手机/关联会员昵称/会员手机 搜索现有代理人
  const fetchAgentReferrerOpts = async (q) => {
    agentForm.setFieldsValue({ referrer_user_id: null, parent_agent_id: null });
    const query = (q || '').trim();
    agentReferrerQRef.current = query;
    if (!query) { setAgentReferrerOpts([]); return; }
    try {
      const res = await axios.get('/ts/admin/agents/search', {
        headers: authHeaders(),
        params: { q: query, exclude: editingAgent?.id || 0 },
      });
      if (agentReferrerQRef.current !== query) return; // 丢弃过期结果
      if (res.data.success) {
        setAgentReferrerOpts((res.data.agents || []).map((a) => ({
          value: a.agent_name,
          label: `${a.agent_name}（${a.phone || a.member_phone || '无手机'} · ${a.agent_level}）`,
          agentId: a.id,
          memberId: a.user_id,
        })));
      }
    } catch (e) { /* ignore */ }
  };

  const openAddAgent = (parent = null) => {
    setEditingAgent(null);
    setAgentParent(parent);
    agentForm.resetFields();
    setAgentNameOpts([]);
    setAgentReferrerOpts([]);
    if (parent) {
      // 新增下级代理：推荐人预置为该代理，并记录上级
      agentForm.setFieldsValue({ referrer_name: parent.agent_name, parent_agent_id: parent.id, referrer_user_id: null });
    }
    setAgentModal(true);
  };

  const openEditAgent = (record) => {
    setEditingAgent(record);
    setAgentParent(null);
    setAgentNameOpts([]);
    setAgentReferrerOpts([]);
    agentForm.setFieldsValue({
      ...record,
      // 总推荐金额默认等于他推荐客户的实际 TS 总额
      total_referral_amount: parseFloat(record.network_ts_total || 0),
      last_settlement_at: record.last_settlement_at ? dayjs(record.last_settlement_at) : null,
    });
    setAgentModal(true);
  };

  const deleteAgent = async (id) => {
    await axios.delete(`/ts/admin/agents/${id}`, { headers: authHeaders() });
    message.success('已删除');
    refresh();
  };

  // 查看某代理推荐的客户清单
  const openReferred = async (agent) => {
    setReferredAgent(agent);
    setReferredModal(true);
    setReferredLoading(true);
    try {
      const res = await axios.get(`/ts/admin/agents/${agent.id}/referred-customers`, { headers: authHeaders() });
      if (res.data.success) setReferredList(res.data.customers || []);
    } catch (e) {
      message.error('获取推荐客户失败');
    } finally {
      setReferredLoading(false);
    }
  };

  // 查看某代理人的下级代理清单
  const openSubAgents = async (agent) => {
    setSubParent(agent);
    setSubModal(true);
    setSubLoading(true);
    try {
      const res = await axios.get(`/ts/admin/agents/${agent.id}/subagents`, { headers: authHeaders() });
      if (res.data.success) setSubList(res.data.subAgents || []);
    } catch (e) {
      message.error('获取下级代理失败');
    } finally {
      setSubLoading(false);
    }
  };

  const renderAgentCard = (a) => (
    <Col xs={24} sm={12} lg={8} key={a.id}>
      <Card size="small" style={{ borderRadius: 12, height: '100%' }} styles={{ body: { padding: 14 } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {a.region ? <Tag color="cyan">{a.region}</Tag> : null}
              <span style={{ fontSize: 16, fontWeight: 600 }}>{a.agent_name}</span>
            </div>
            <div style={{ marginTop: 4 }}><Tag color="blue">{a.agent_level}{levelRateText(a.agent_level) ? ` · ${levelRateText(a.agent_level)}` : ''}</Tag></div>
          </div>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>会员：{a.member_nickname || '-'}</Text>
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: '#595959', lineHeight: 2 }}>
          <div>📱 手机：{a.phone || '-'}</div>
          <div>⬆️ 上级：{a.referrer_name || a.parent_agent_name || '-'}{a.referrer_user_id ? <Tag color="blue" style={{ marginInlineStart: 4 }}>会员</Tag> : null}</div>
          <div>👥 推荐客户：{(a.customer_count || 0) > 0
            ? <a onClick={() => openReferred(a)}>{a.customer_count} 人 / ¥{parseFloat(a.network_ts_total || 0).toFixed(0)}</a>
            : <span>0 人 / ¥0.00</span>}
            <Text type="secondary" style={{ fontSize: 12 }}>（总推荐金额）</Text>
          </div>
          <div>🧾 推荐费：<Text strong style={{ color: '#722ed1' }}>¥{agentRefFee(a).toFixed(0)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}> · 推荐代理奖励 ¥{agentRecruitReward(a).toFixed(0)} · 下级贡献 ¥{agentDownlineReward(a).toFixed(0)}</Text>
          </div>
          <div>💎 总收益：<Text strong style={{ color: '#f5222d' }}>¥{agentTotalReward(a).toFixed(0)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>（推荐费+推荐代理奖励+下级贡献）</Text>
          </div>
          <div>💰 结算：未结算 <Text strong style={{ color: '#fa8c16' }}>¥{agentUnsettled(a).toFixed(0)}</Text>（总收益−已结算）· 已结算 ¥{parseFloat(a.settled_fee || 0).toFixed(0)}</div>
          <div>👥 下级代理：{(a.sub_agent_count || 0) > 0
            ? <a onClick={() => openSubAgents(a)}>{a.sub_agent_count} 人（查看）</a>
            : <span>0 人</span>}</div>
          <div>🕒 最近结算：{a.last_settlement_at ? dayjs(a.last_settlement_at).format('YYYY-MM-DD') : '-'}</div>
        </div>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, borderTop: '1px solid #f5f5f5', paddingTop: 8 }}>
          <Button type="link" size="small" style={{ color: '#13c2c2' }} icon={<TeamOutlined />} onClick={() => openSubAgents(a)}>下级代理</Button>
          <Button type="link" size="small" style={{ color: '#1677ff' }} icon={<UserAddOutlined />} onClick={() => openAddAgent(a)}>新增下级</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditAgent(a)}>修改</Button>
          <Popconfirm title="删除该代理人？" onConfirm={() => deleteAgent(a.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </div>
      </Card>
    </Col>
  );

  const agentColumns = [
    { title: '地区', dataIndex: 'region', width: 90, render: (v) => v || '-' },
    { title: '代理人', dataIndex: 'agent_name' },
    { title: '手机号', dataIndex: 'phone' },
    { title: '级别', dataIndex: 'agent_level', width: 130, render: (l) => <Tag color="blue">{l}{levelRateText(l) ? ` · ${levelRateText(l)}` : ''}</Tag> },
    {
      title: '上级',
      key: 'agent_referrer',
      render: (_, r) => {
        const name = r.referrer_name || r.parent_agent_name;
        if (!name) return '-';
        return r.referrer_user_id
          ? <span>{name} <Tag color="blue" style={{ marginInlineStart: 4 }}>会员</Tag></span>
          : name;
      },
    },
    { title: '会员', render: (_, r) => (r.member_nickname ? `${r.member_nickname}${r.member_phone ? ' (' + r.member_phone + ')' : ''}` : '-') },
    {
      title: '推荐客户',
      key: 'referred_count',
      width: 90,
      render: (_, r) => (
        (r.customer_count || 0) > 0
          ? <a onClick={() => openReferred(r)}>{r.customer_count} 人</a>
          : <span>0 人</span>
      ),
    },
    {
      title: '总推荐金额',
      key: 'referred_total',
      width: 140,
      render: (_, r) => (
        (r.customer_count || 0) > 0
          ? <a style={{ fontWeight: 600 }} onClick={() => openReferred(r)}>¥{parseFloat(r.network_ts_total || 0).toFixed(0)}</a>
          : <Text>¥0.00</Text>
      ),
    },
    {
      title: '推荐费',
      key: 'ref_fee',
      width: 100,
      render: (_, r) => (
        <Text strong style={{ color: '#722ed1' }} title={`总推荐金额 ¥${parseFloat(r.network_ts_total || 0).toFixed(0)} × ${levelRateText(r.agent_level) || '2%'}`}>
          ¥{agentRefFee(r).toFixed(0)}
        </Text>
      ),
    },
    {
      title: '推荐代理奖励',
      key: 'recruit_reward',
      width: 110,
      render: (_, r) => <Text style={{ color: '#eb2f96' }} title="所发展下级代理会员费的 50% 之和">¥{agentRecruitReward(r).toFixed(0)}</Text>,
    },
    {
      title: '下级贡献',
      key: 'downline_reward',
      width: 100,
      render: (_, r) => <Text style={{ color: '#13c2c2' }} title="下级推荐 TS 总额 × 级别奖励率(钻石1%/铂金2%)">¥{agentDownlineReward(r).toFixed(0)}</Text>,
    },
    {
      title: '总收益',
      key: 'total_reward',
      width: 110,
      render: (_, r) => <Text strong style={{ color: '#f5222d' }} title="推荐费 + 推荐代理奖励 + 下级贡献">¥{agentTotalReward(r).toFixed(0)}</Text>,
    },
    {
      title: '未结算',
      key: 'unsettled',
      render: (_, r) => <Text type="warning" title="总收益 − 已结算">¥{agentUnsettled(r).toFixed(0)}</Text>,
    },
    {
      title: '已结算',
      dataIndex: 'settled_fee',
      render: (v) => `¥${parseFloat(v || 0).toFixed(0)}`,
    },
    {
      title: '最近结算',
      dataIndex: 'last_settlement_at',
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-'),
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', flexWrap: 'nowrap', whiteSpace: 'nowrap', gap: 8 }}>
          <a style={{ color: '#13c2c2', fontWeight: 600 }} onClick={() => openSubAgents(record)}>下级代理{record.sub_agent_count ? `(${record.sub_agent_count})` : ''}</a>
          <a style={{ color: '#1677ff' }} onClick={() => openAddAgent(record)}>新增下级</a>
          <a onClick={() => openEditAgent(record)}>修改</a>
          <Popconfirm title="删除该代理人？" onConfirm={() => deleteAgent(record.id)}>
            <a style={{ color: '#ff4d4f' }}>删除</a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={8}><Card><Statistic title="TS 客户" value={stats.customerCount || 0} /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title="TS 代理人" value={stats.agentCount || 0} /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title="客户 TS 总额" value={stats.totalTsAmount || 0} precision={0} prefix="¥" /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title="代理总收益" value={stats.totalReward || 0} precision={0} prefix="¥" valueStyle={{ color: '#f5222d' }} /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title="已结算收益" value={stats.totalSettledFee || 0} precision={0} prefix="¥" valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title="未结算收益" value={stats.totalUnsettledFee || 0} precision={0} prefix="¥" valueStyle={{ color: '#fa8c16' }} /></Card></Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'customers',
              label: 'TS 客户管理',
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    <Button type="primary" icon={<UserAddOutlined />} onClick={openAddCustomer}>
                      新增客户
                    </Button>
                    <Space wrap>
                      <Button
                        icon={detailExpanded ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
                        onClick={() => setDetailExpanded((v) => !v)}
                      >
                        {detailExpanded ? '收起明细' : '展开明细'}
                      </Button>
                      <Segmented
                        value={customerView}
                        onChange={changeCustomerView}
                        options={[
                          { label: '卡片', value: 'card', icon: <AppstoreOutlined /> },
                          { label: '列表', value: 'table', icon: <BarsOutlined /> },
                        ]}
                      />
                      <Button icon={<SettingOutlined />} onClick={() => { setEditingType(null); typeForm.resetFields(); setTypeMgrModal(true); }}>
                        类型管理
                      </Button>
                    </Space>
                  </div>
                  {customerView === 'card' ? (
                    customers.length === 0
                      ? <Empty description="暂无客户" />
                      : <Row gutter={[12, 12]}>{customers.map(renderCustomerCard)}</Row>
                  ) : (
                    <Table rowKey="id" loading={loading} columns={displayedCustomerColumns} dataSource={customers} scroll={detailExpanded ? { x: 1700 } : undefined} />
                  )}
                </>
              ),
            },
            {
              key: 'agents',
              label: 'TS 代理人管理',
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    <Button type="primary" icon={<UserAddOutlined />} onClick={() => openAddAgent()}>
                      新增代理人
                    </Button>
                    <Space wrap>
                    <Button style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)', color: '#fff', border: 'none' }} onClick={() => setDemoOpen(true)}>
                      🚀 收益演示
                    </Button>
                    <Segmented
                      value={agentView}
                      onChange={changeAgentView}
                      options={[
                        { label: '卡片', value: 'card', icon: <AppstoreOutlined /> },
                        { label: '列表', value: 'table', icon: <BarsOutlined /> },
                      ]}
                    />
                    </Space>
                  </div>
                  {agentView === 'card' ? (
                    agents.length === 0
                      ? <Empty description="暂无代理人" />
                      : <Row gutter={[12, 12]}>{agents.map(renderAgentCard)}</Row>
                  ) : (
                    <Table rowKey="id" loading={loading} columns={agentColumns} dataSource={agents} scroll={{ x: 1200 }} />
                  )}
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* 选会员 */}
      <Modal title="选择已注册会员" open={memberModal} onCancel={() => setMemberModal(false)} footer={null} width={600}>
        <Input.Search placeholder="搜索昵称/手机号/ID" onSearch={searchMembers} onChange={(e) => setMemberSearch(e.target.value)} enterButton style={{ marginBottom: 16 }} />
        <List
          dataSource={members}
          locale={{ emptyText: '无可用会员（可能已全部关联）' }}
          renderItem={(m) => (
            <List.Item actions={[<Button type="link" onClick={() => selectMember(m)}>选择</Button>]}>
              <List.Item.Meta
                title={`${m.nickname} (ID:${m.id})`}
                description={`${m.phone || '无手机号'} · ${m.member_level}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 客户表单 */}
      <Modal
        title={editingCustomer ? '编辑 TS 客户' : '新增 TS 客户'}
        open={customerModal}
        onCancel={() => { setCustomerModal(false); customerForm.resetFields(); }}
        onOk={() => customerForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form form={customerForm} layout="vertical" onFinish={saveCustomer}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户姓名" rules={[{ required: true }]}>
                <Input placeholder="直接填写客户姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="region" label="地区">
                <Input placeholder="如：广东深圳" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="id_card" label="身份证号"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="手机号"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="year" label="年份">
                <Select allowClear showSearch placeholder="选择年份" options={yearOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ts_amount" label="TS 金额" initialValue={0}>
                <InputNumber
                  min={0}
                  precision={0}
                  style={{ width: '100%' }}
                  prefix="¥"
                  onChange={(v) => customerForm.setFieldsValue({ fee_amount: v != null ? Math.round(v * 0.24 * 100) / 100 : 0 })}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_type" label="客户类型" initialValue="个人" tooltip="在右侧「类型管理」中维护可选类型">
                <Select
                  placeholder="选择客户类型"
                  options={customerTypes.map((t) => ({ value: t.type_name, label: t.type_name }))}
                  showSearch
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="fee_amount" label="手续费" initialValue={0} tooltip="默认按 TS金额的 24% 自动计算，可手动调整">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} prefix="¥" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="paid_amount" label="已付" initialValue={0}>
                <InputNumber min={0} precision={0} style={{ width: '100%' }} prefix="¥" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="未付" tooltip="手续费 − 已付，自动计算">
                <Form.Item noStyle shouldUpdate={(p, c) => p.fee_amount !== c.fee_amount || p.paid_amount !== c.paid_amount}>
                  {() => {
                    const unpaid = (Number(customerForm.getFieldValue('fee_amount')) || 0) - (Number(customerForm.getFieldValue('paid_amount')) || 0);
                    return <InputNumber value={unpaid} precision={0} disabled style={{ width: '100%' }} prefix="¥" />;
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          {editingCustomer && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="refund_amount" label="实退金额" initialValue={0}>
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} prefix="¥" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name="bank_card" label="银行卡" tooltip="文本格式，可含空格或连字符，保留前导 0">
                    <Input placeholder="银行卡号（文本）" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="refund_note" label="实退提示" tooltip="将用于「发送」话术中的“具体金额为：”部分">
                <Input placeholder="如：5000元 / 已到账 3200 元" />
              </Form.Item>
            </>
          )}
          <Form.Item name="referrer_user_id" hidden><Input /></Form.Item>
          <Form.Item name="referrer_name" label="推荐人" tooltip="可直接输入推荐人姓名，或搜索改选为某个已注册会员">
            <AutoComplete
              options={referrerOptions}
              onSearch={fetchReferrerOptions}
              onSelect={(val, option) => customerForm.setFieldsValue({ referrer_user_id: option?.memberId || null })}
              placeholder="输入推荐人姓名，或搜索已注册会员"
              filterOption={false}
              allowClear
            />
          </Form.Item>
          <Form.Item name="basic_profile" label="基本资料">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 代理人表单 */}
      <Modal
        title={editingAgent ? '编辑 TS 代理人' : (agentParent ? `新增下级代理（上级：${agentParent.agent_name}）` : '新增代理人')}
        open={agentModal}
        onCancel={() => { setAgentModal(false); setAgentParent(null); agentForm.resetFields(); }}
        onOk={() => agentForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form form={agentForm} layout="vertical" onFinish={saveAgent}>
          <Form.Item name="user_id" hidden><Input /></Form.Item>
          <Form.Item name="referrer_user_id" hidden><Input /></Form.Item>
          <Form.Item name="parent_agent_id" hidden><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="region" label="地区">
                <Input placeholder="如：广东深圳" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="agent_name" label="代理人姓名" rules={[{ required: true }]} tooltip="可直接输入，或搜索选择已注册会员">
                <AutoComplete
                  options={agentNameOpts}
                  onSearch={fetchAgentNameOpts}
                  onSelect={(val, option) => agentForm.setFieldsValue({ user_id: option?.memberId || null })}
                  placeholder="输入姓名，或搜索已注册会员"
                  filterOption={false}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="phone" label="手机号"><Input /></Form.Item></Col>
            <Col span={12}>
              <Form.Item name="agent_level" label="级别" initialValue="普通代理" tooltip="费用=升级到该级别的费用；百分比=推荐手续费费率">
                <Select>{agentLevels.map((l) => <Option key={l} value={l}>{l}{AGENT_FEE_PRICE[l] ? `（¥${AGENT_FEE_PRICE[l]} · ${levelRateText(l)}）` : ''}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="referrer_name" label="上级" tooltip="该代理的上级代理（可获下级 50% 推荐费 + 下级客户TS额的 1%）。按 姓名/手机号 搜索现有代理人选择">
            <AutoComplete
              options={agentReferrerOpts}
              onSearch={fetchAgentReferrerOpts}
              onSelect={(val, option) => agentForm.setFieldsValue({ referrer_user_id: option?.memberId || null, parent_agent_id: option?.agentId || null })}
              placeholder="按姓名或手机号搜索上级代理人"
              filterOption={false}
              allowClear
              disabled={!!agentParent}
            />
          </Form.Item>

          {editingAgent && (
            <>
              <Form.Item name="id_card" label="身份证号"><Input /></Form.Item>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="total_referral_amount" label="总推荐金额" initialValue={0}>
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="总收益" tooltip="推荐费 + 推荐代理奖励 + 下级贡献（自动，只读）" shouldUpdate>
                    {({ getFieldValue }) => {
                      const total = parseFloat(getFieldValue('total_referral_amount') || 0);
                      const fee = total * agentRate(getFieldValue('agent_level'));
                      const recruit = parseFloat(editingAgent?.recruit_reward || 0);
                      const down = parseFloat(editingAgent?.downline_reward || 0);
                      return <InputNumber value={Math.round(fee + recruit + down)} disabled style={{ width: '100%', color: '#f5222d' }} />;
                    }}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="settled_fee" label="已结算推荐费" initialValue={0}
                    tooltip="只需填已结算；未结算 = 总收益 − 已结算，系统自动计算">
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const total = parseFloat(getFieldValue('total_referral_amount') || 0);
                  const level = getFieldValue('agent_level');
                  const settled = parseFloat(getFieldValue('settled_fee') || 0);
                  const fee = total * agentRate(level);
                  const recruit = parseFloat(editingAgent?.recruit_reward || 0);
                  const down = parseFloat(editingAgent?.downline_reward || 0);
                  const totalReward = fee + recruit + down;
                  return (
                    <div style={{ margin: '-4px 0 12px', padding: '8px 12px', background: '#faf9ff', border: '1px solid #efeafd', borderRadius: 8, fontSize: 13, color: '#595959' }}>
                      推荐费 ¥{fee.toFixed(0)} + 推荐代理奖励 ¥{recruit.toFixed(0)} + 下级贡献 ¥{down.toFixed(0)} = 总收益 <b style={{ color: '#f5222d' }}>¥{totalReward.toFixed(0)}</b>
                      <span style={{ margin: '0 8px', color: '#d9d9d9' }}>|</span>
                      未结算 = 总收益 − 已结算 = <b style={{ color: '#fa8c16' }}>¥{(totalReward - settled).toFixed(0)}</b>
                    </div>
                  );
                }}
              </Form.Item>
              <Form.Item name="last_settlement_at" label="最近一次结算时间">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* 跟进记录 */}
      <Modal
        title={`跟进记录 - ${followupCustomer?.customer_name || ''}`}
        open={followupModal}
        onCancel={() => setFollowupModal(false)}
        footer={null}
        width={700}
      >
        <Form form={followupForm} layout="inline" onFinish={addFollowup} style={{ marginBottom: 16 }}>
          <Form.Item name="followup_date" initialValue={dayjs()}>
            <DatePicker />
          </Form.Item>
          <Form.Item name="content" rules={[{ required: true, message: '请填写内容' }]} style={{ flex: 1 }}>
            <Input placeholder="跟进内容" style={{ width: 320 }} />
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" icon={<PlusOutlined />}>添加</Button></Form.Item>
        </Form>
        <List
          dataSource={followups}
          renderItem={(item) => (
            <List.Item actions={[
              <Popconfirm key="del" title="删除？" onConfirm={async () => {
                await axios.delete(`/ts/admin/followups/${item.id}`, { headers: authHeaders() });
                openFollowups(followupCustomer);
              }}>
                <Button type="link" danger size="small">删除</Button>
              </Popconfirm>,
            ]}>
              <List.Item.Meta
                title={dayjs(item.followup_date).format('YYYY-MM-DD')}
                description={`${item.content} · 操作人: ${item.operator_name || '-'}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 客户类型管理 */}
      <Modal
        title="TS 客户类型管理"
        open={typeMgrModal}
        onCancel={() => { setTypeMgrModal(false); setEditingType(null); typeForm.resetFields(); }}
        footer={null}
        width={480}
      >
        <Form form={typeForm} layout="inline" onFinish={submitType} style={{ marginBottom: 16 }}>
          <Form.Item name="type_name" rules={[{ required: true, message: '请输入类型名称' }]} style={{ flex: 1 }}>
            <Input placeholder="类型名称，如 个人 / 企业 / 高净值客户" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">{editingType ? '更新' : '添加'}</Button>
            {editingType && (
              <Button style={{ marginInlineStart: 8 }} onClick={() => { setEditingType(null); typeForm.resetFields(); }}>取消</Button>
            )}
          </Form.Item>
        </Form>
        <List
          size="small"
          bordered
          dataSource={customerTypes}
          locale={{ emptyText: '暂无类型，请在上方添加' }}
          renderItem={(t) => (
            <List.Item actions={[
              <Button key="edit" type="link" size="small" icon={<EditOutlined />}
                onClick={() => { setEditingType(t); typeForm.setFieldsValue({ type_name: t.type_name }); }}>编辑</Button>,
              <Popconfirm key="del" title="删除该类型？（不影响已保存客户的类型文本）" onConfirm={() => deleteType(t.id)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>,
            ]}>
              {t.type_name}
            </List.Item>
          )}
        />
      </Modal>

      {/* 某代理推荐的客户清单（详细资料） */}
      <Modal
        title={`推荐客户清单 - ${referredAgent?.agent_name || ''}`}
        open={referredModal}
        onCancel={() => { setReferredModal(false); setReferredList([]); }}
        footer={null}
        width={1100}
      >
        <div style={{ marginBottom: 12 }}>
          <Text>共 <Text strong>{referredList.length}</Text> 位客户，TS 金额合计 <Text strong style={{ color: '#6366F1' }}>¥{referredList.reduce((s, c) => s + parseFloat(c.ts_amount || 0), 0).toFixed(0)}</Text></Text>
        </div>
        <Table
          rowKey="id"
          size="small"
          loading={referredLoading}
          dataSource={referredList}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: 1300 }}
          columns={customerColumns.filter((c) => c.key !== 'action')}
        />
      </Modal>

      {/* 某代理人的下级代理清单 */}
      <Modal
        title={`下级代理 - ${subParent?.agent_name || ''}`}
        open={subModal}
        onCancel={() => { setSubModal(false); setSubList([]); }}
        footer={null}
        width={720}
      >
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Text>共 <Text strong>{subList.length}</Text> 个下级代理</Text>
          <Button type="primary" size="small" icon={<UserAddOutlined />}
            onClick={() => { setSubModal(false); openAddAgent(subParent); }}>在此代理下新增下级</Button>
        </div>
        <Table
          rowKey="id"
          size="small"
          loading={subLoading}
          dataSource={subList}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: 560 }}
          locale={{ emptyText: '暂无下级代理' }}
          columns={[
            { title: '代理人', dataIndex: 'agent_name' },
            { title: '级别', dataIndex: 'agent_level', width: 90, render: (l) => <Tag color="blue">{(l || '').replace('代理', '')}</Tag> },
            { title: '地区', dataIndex: 'region', width: 90, render: (v) => v || '-' },
            { title: '手机号', dataIndex: 'phone', render: (v) => v || '-' },
            { title: '推荐客户', dataIndex: 'customer_count', width: 80, align: 'center', render: (v) => `${v || 0}` },
            { title: 'TS 金额', dataIndex: 'network_ts_total', align: 'right', render: (v) => <Text strong style={{ color: '#1890ff' }}>¥{parseFloat(v || 0).toFixed(0)}</Text> },
          ]}
        />
      </Modal>

      <AgentTierDemo open={demoOpen} onClose={() => setDemoOpen(false)} admin />
    </div>
  );
};

export default TsAgentAdminPanel;
